import React, { useState } from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { EventProvider } from '@/types';

interface ProviderMultiSelectProps {
  options: EventProvider[];
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
}

/**
 * Multi-select for event providers, used by NewTaskModal and TaskDetailModal.
 * Value is the list of selected `evento_proveedores.id` strings — that's the
 * FK shape `tarea_proveedores` stores, so no conversion is needed at the
 * service boundary.
 */
export const ProviderMultiSelect: React.FC<ProviderMultiSelectProps> = ({
  options,
  value,
  onChange,
  disabled = false,
  placeholder = 'Seleccionar proveedores...',
}) => {
  const [open, setOpen] = useState(false);

  const selectedOptions = options.filter((o) => value.includes(o.id));

  const toggle = (id: string) => {
    if (value.includes(id)) {
      onChange(value.filter((v) => v !== id));
    } else {
      onChange([...value, id]);
    }
  };

  const remove = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(value.filter((v) => v !== id));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between min-h-10 h-auto py-2"
        >
          <div className="flex flex-wrap gap-1 items-center flex-1 text-left">
            {selectedOptions.length === 0 ? (
              <span className="text-muted-foreground text-sm">{placeholder}</span>
            ) : (
              selectedOptions.map((o) => (
                <Badge key={o.id} variant="secondary" className="gap-1 max-w-full">
                  <span className="truncate" title={`${o.provider?.tipo ?? ''} · ${o.provider?.nombre ?? ''}`}>
                    {o.provider?.tipo ? `${o.provider.tipo}: ` : ''}
                    {o.provider?.nombre ?? ''}
                  </span>
                  {!disabled && (
                    <span
                      role="button"
                      aria-label={`Quitar ${o.provider?.nombre ?? 'proveedor'}`}
                      onClick={(e) => remove(o.id, e)}
                      className="cursor-pointer hover:text-red-600"
                    >
                      <X className="w-3 h-3" />
                    </span>
                  )}
                </Badge>
              ))
            )}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar proveedor..." />
          <CommandList>
            <CommandEmpty>No hay proveedores asignados a este evento.</CommandEmpty>
            <CommandGroup>
              {options.map((o) => {
                const isSelected = value.includes(o.id);
                return (
                  <CommandItem
                    key={o.id}
                    value={`${o.provider?.tipo ?? ''} ${o.provider?.nombre ?? ''}`}
                    onSelect={() => toggle(o.id)}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        isSelected ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    <span className="flex-1 truncate">
                      {o.provider?.tipo ? (
                        <>
                          <span className="text-xs text-gray-500">{o.provider.tipo}: </span>
                          {o.provider.nombre}
                        </>
                      ) : (
                        o.provider?.nombre ?? '(Sin nombre)'
                      )}
                    </span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
